import React, { RefObject, useMemo } from 'react'
import { Formik, FormikHelpers, FormikProps, useFormikContext } from 'formik'
import { DosingLimits } from '../shared/dosingLimits'
import FormSection from '../shared/forms/FormSection'
import {
  CheckboxField,
  SelectField,
  TextField,
  TextareaField
} from '../shared/forms/FormFields'
import { FormReadOnlyContext } from '../shared/forms/FormReadOnlyContext'
import { SelectOption } from '../shared/forms/SelectOption'
import {
  NumberField,
  Panel,
  PanelSection,
  ParameterFields,
  modeLabel
} from './FeederDosingModeFields'
import {
  EmergencyStepsTable,
  LevelParametersTable
} from './FeederDosingModeTables'
import { feederDosingModeSchema } from './feederDosingModeSchema'
import { FILL_LEVELS, FillLevel } from '../shared/dosing_modes/fillLevels'
import { FeederDosingModeValues } from './feederDosingModeValues'

const FILL_LEVEL_OPTIONS: SelectOption<FillLevel>[] = FILL_LEVELS.map(
  (fillLevel) => ({ value: fillLevel, label: modeLabel(`levels.${fillLevel}`) })
)

const FillBoundaries = () => (
  <FormSection
    title={modeLabel('fill_boundaries')}
    tooltip={modeLabel('fill_boundaries_info')}
  >
    <div className="dosing-mode-form__fields">
      <NumberField
        name="high_fill_min_percent"
        label={modeLabel('high_fill_min_percent')}
        placeholder={modeLabel('placeholders.high_fill_min_percent')}
        unit="percent"
        decimals={0}
        inverted
      />
      <NumberField
        name="medium_fill_min_percent"
        label={modeLabel('medium_fill_min_percent')}
        placeholder={modeLabel('placeholders.medium_fill_min_percent')}
        unit="percent"
        decimals={0}
        inverted
      />
      <SelectField
        name="unknown_fill_fallback"
        label={modeLabel('unknown_fill_fallback')}
        tooltip={modeLabel('unknown_fill_fallback_info')}
        placeholder={modeLabel('select_unknown_fill_fallback')}
        options={FILL_LEVEL_OPTIONS}
        inverted
      />
    </div>
  </FormSection>
)

type BlowerSettingName = 'standard_blower_setting' | 'emergency_blower_setting'

const BlowerSection = ({ name }: { name: BlowerSettingName }) => {
  const { values } = useFormikContext<FeederDosingModeValues>()
  const disabled = !values[name].enabled

  return (
    <PanelSection
      title={modeLabel('blower')}
      hint={modeLabel(`${name}_hint`)}
      toggle={
        <CheckboxField
          name={`${name}.enabled`}
          label={modeLabel('blower_enabled')}
          variant="switch"
        />
      }
    >
      <div className="dosing-mode-form__fields">
        <NumberField
          name={`${name}.air_pressure`}
          label={modeLabel('air_pressure')}
          unit="bar"
          disabled={disabled}
        />
        <NumberField
          name={`${name}.blowing_duration_on_ms`}
          label={modeLabel('blowing_duration_on_ms')}
          unit="s"
          decimals={3}
          disabled={disabled}
        />
        <NumberField
          name={`${name}.blowing_duration_off_ms`}
          label={modeLabel('blowing_duration_off_ms')}
          unit="s"
          decimals={3}
          disabled={disabled}
        />
        <NumberField
          name={`${name}.activation_delay_ms`}
          label={modeLabel('activation_delay_ms')}
          unit="s"
          decimals={3}
          disabled={disabled}
        />
      </div>
    </PanelSection>
  )
}

const StandardDosingPanel = () => (
  <Panel title={modeLabel('standard_dosing')}>
    <LevelParametersTable />
    <BlowerSection name="standard_blower_setting" />
  </Panel>
)

const EmergencyPanel = ({ maxSteps }: { maxSteps: number }) => (
  <Panel title={modeLabel('emergency')}>
    <div className="dosing-mode-form__fields dosing-mode-form__fields--three">
      <NumberField
        name="emergency_setting.max_attempts"
        label={modeLabel('max_attempts')}
        decimals={0}
      />
      <NumberField
        name="emergency_setting.max_dosing_time_ms"
        label={modeLabel('max_dosing_time_ms')}
        unit="s"
        decimals={3}
      />
      <NumberField
        name="emergency_setting.low_fill_max_dosing_time_ms"
        label={modeLabel('low_fill_max_dosing_time_ms')}
        unit="s"
        decimals={3}
      />
      <NumberField
        name="emergency_setting.no_change_max_time_ms"
        label={modeLabel('no_change_max_time_ms')}
        unit="s"
        decimals={3}
      />
      <NumberField
        name="emergency_setting.no_change_amount"
        label={modeLabel('no_change_amount')}
        unit="g"
        decimals={0}
      />
      <NumberField
        name="emergency_setting.residual_delivery_amount"
        label={modeLabel('residual_delivery_amount')}
        unit="g"
        decimals={0}
      />
    </div>
    <EmergencyStepsTable maxSteps={maxSteps} />
    <BlowerSection name="emergency_blower_setting" />
  </Panel>
)

const PartialCompletionPanel = () => (
  <Panel title={modeLabel('partial_completion')}>
    <p className="text-muted">{modeLabel('partial_completion_hint')}</p>
    <div className="dosing-mode-form__fields dosing-mode-form__fields--three">
      <NumberField
        name="max_acceptable_shortfall_percent"
        label={modeLabel('max_acceptable_shortfall_percent')}
        unit="percent"
        decimals={0}
      />
    </div>
  </Panel>
)

const PusherPanel = () => (
  <Panel
    title={modeLabel('pusher')}
    toggle={
      <CheckboxField
        name="pusher_enabled"
        label={modeLabel('pusher_enabled')}
        variant="switch"
      />
    }
  >
    <p className="text-muted mb-0">{modeLabel('pusher_hint')}</p>
  </Panel>
)

const GoBackPanel = () => {
  const { values } = useFormikContext<FeederDosingModeValues>()
  const disabled = !values.go_back_enabled

  return (
    <Panel
      title={modeLabel('go_back')}
      toggle={
        <CheckboxField
          name="go_back_enabled"
          label={modeLabel('go_back_enabled')}
          variant="switch"
        />
      }
    >
      <div className="dosing-mode-form__fields">
        <NumberField
          name="go_back_duration_ms"
          label={modeLabel('go_back_duration_ms')}
          unit="s"
          decimals={3}
          disabled={disabled}
        />
        <ParameterFields prefix="go_back_parameter" disabled={disabled} />
      </div>
    </Panel>
  )
}

// Number fields commit to Formik on blur, but Enter submits without blurring. Blur the focused field
// first, then submit on the next tick: Formik reads the values of its last render, so submitting
// straight away would still send the field's old value.
const submitWithFocusedFieldCommitted = (
  event: React.FormEvent<HTMLFormElement>,
  submitForm: () => Promise<void>
) => {
  event.preventDefault()
  const focused = document.activeElement

  if (
    focused instanceof HTMLInputElement &&
    event.currentTarget.contains(focused)
  ) {
    focused.blur()
    setTimeout(submitForm)
  } else {
    submitForm()
  }
}

export const FORM_ID = 'feeder-dosing-mode-form'

interface Props {
  initialValues: FeederDosingModeValues
  limits: DosingLimits
  readOnly: boolean
  formikRef: RefObject<FormikProps<FeederDosingModeValues>>
  onSubmit: (
    values: FeederDosingModeValues,
    helpers: FormikHelpers<FeederDosingModeValues>
  ) => Promise<void>
}

// The Save button lives in the versioning actions bar and targets the form by id, so the submit
// event still passes through the blur-then-submit handler
const FeederDosingModeForm = ({
  initialValues,
  limits,
  readOnly,
  formikRef,
  onSubmit
}: Props) => {
  const validationSchema = useMemo(
    () => feederDosingModeSchema(limits),
    [limits]
  )

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ submitForm }) => (
        <form
          id={FORM_ID}
          noValidate
          onSubmit={(event) =>
            submitWithFocusedFieldCommitted(event, submitForm)
          }
          className="form-robis dosing-mode-form"
        >
          <FormReadOnlyContext.Provider value={readOnly}>
            <TextField
              name="name"
              label={modeLabel('name')}
              placeholder={modeLabel('enter_name')}
            />
            <TextareaField
              name="description"
              label={modeLabel('description')}
              placeholder={modeLabel('enter_description')}
            />

            <FillBoundaries />

            <StandardDosingPanel />
            <EmergencyPanel maxSteps={limits.emergency_steps_max} />
            <PartialCompletionPanel />
            <GoBackPanel />
            <PusherPanel />
          </FormReadOnlyContext.Provider>
        </form>
      )}
    </Formik>
  )
}

export default FeederDosingModeForm
